<template>
  <div class="w-full">
    <div class="flex justify-between items-center mb-4 lg:mt-4">
      <!-- 返回按鈕 -->
      <button @click="goBack" class="w-fit sm:w-auto inline-flex items-center btn rounded-full text-12px mt-2 ml-1 sm:mt-0 py-1 pl-2 pr-4 border border-gray-100 hover:bg-gray-500">
        <img src="../../images/icons/back-page.svg" class='h-4 w-4 inline-block' />
        <div class="mr-1">
          {{ $t('profiles.backed_campaigns.back_to_campaigns') }}
        </div>
      </button>
    </div>
    <!-- 載入狀態 -->
    <div v-if="state.loading" class="flex justify-center items-center mt-[15%]">
      <img src="../../images/icons/loading.png" class="w-6 h-6 icon loading-icon"/>
    </div>

    <!-- 錯誤狀態 -->
    <div v-else-if="state.error" class="flex flex-col items-center justify-center mt-[15%] text-center">
      <img src="../../images/file.svg" class="block w-12 h-12 mb-4" />
      <h3 class="text-16px font-medium mb-2">
        {{ state.error }}
      </h3>
      <button @click="goBack" class="btn btn-primary mt-4">
        {{ $t('profiles.backed_campaigns.back_to_campaigns') }}
      </button>
    </div>

    <!-- 主要內容 -->
    <div v-else-if="orderData" class="flex flex-col smd:flex-row gap-4 items-start">
      <!-- Rewards -->
      <div class="w-full smd:max-w-[240px] lg:max-w-[314px] h-auto bg-gray-600 rounded-xl p-6 space-y-2">
        <div
          v-for="rewardData in orderData.campaign_data.grouped_rewards"
          :key="rewardData.reward.id"
          class="reward-item">

          <!-- Reward Image -->
          <div v-if="rewardData.reward.cover_image" class="mb-4">
            <img
              :src="getImageUrl(rewardData.reward.cover_image)"
              :alt="rewardData.reward.title"
              class="w-full h-48 object-cover rounded-lg" />
          </div>

          <!-- Reward Info -->
          <div class="flex items-center gap-2">
            <span class="text-14px line-clamp-2">
              {{ rewardData.reward.title }}
            </span>
            <span class="text-14px ">
              x{{ rewardData.count }}
            </span>
          </div>
        </div>

        <hr class="border-gray-200 mt-4">
        <div class="flex flex items-center justify-start">
          <span class="text-12px text-gray-150">
            <img src="../../images/icons/calendar.svg" class="h-5 w-5 inline-block opacity-50">
            {{ $t('profiles.backed_campaigns.estimated_delivery') }}: {{ formatDate(latestDeliveryDate) }}
          </span>
        </div>

        <!-- 如果沒有 rewards -->
        <div v-if="!orderData.campaign_data.grouped_rewards.length" class="bg-gray-600 rounded-xl p-6 text-center">
          <p class="text-gray-400">{{ $t('profiles.backed_campaigns.no_rewards_found') }}</p>
        </div>
      </div>

      <!-- Pledge Overview -->
      <div class="w-full smd:w-2/3">
        <div class="bg-gray-600 rounded-xl p-6 lg:sticky lg:top-6">
          <!-- Header with Edit Button -->
          <div class="flex justify-between items-center mb-6">
            <h2 class="font-bold text-gray-150">
              {{ $t('profiles.backed_campaigns.pledge_overview') }}
            </h2>

            <div class="status flex items-center text-14px">
              <span
                class="font-medium text-center inline-block py-1 px-2 rounded-full border text-14px"
                :style="statusInfo.style"
              >
                {{ statusInfo.text }}
              </span>
            </div>
          </div>

          <div class="p-4 rounded-4px bg-bg-base">
            <h3 class="text-14px font-bold mb-4">{{ campaignTitle }}</h3>
            <!-- Basic Info -->
            <div class="mb-4 p-4 rounded-4px bg-gray-600 text-14px space-y-4">
              <p class="text-16px font-bold"> {{ $t('profiles.backed_campaigns.basic') }} </p>
              <div class="flex flex-col">
                <span class="text-gray-100 font-bold mb-1"> {{ $t('profiles.backed_campaigns.backer_name') }}</span>
                <span class="text-gray-150">{{ backerName }}</span>
              </div>
              <div class="flex flex-col">
                <span class="text-gray-100 font-bold mb-1"> {{ $t('profiles.backed_campaigns.email') }}</span>
                <span class="text-gray-150">{{ backerEmail }}</span>
              </div>
              <div class="flex flex-col">
                <span class="text-gray-100 font-bold mb-1"> {{ $t('profiles.backed_campaigns.campaign_order_id') }}</span>
                <span class="text-gray-150">{{ backerId }}</span>
              </div>
            </div>

            <!-- Pledge Detail -->
            <div class="mb-4 p-4 rounded-4px bg-gray-600 text-14px space-y-4">
              <p class="text-16px font-bold"> {{ $t('profiles.backed_campaigns.pledge_detail') }} </p>
              <p class="text-14px font-bold text-gray-100"> {{ $t('profiles.backed_campaigns.pledge_amount') }} </p>
              <!-- 每個 reward 的明細 -->
              <div
                v-for="rewardData in orderData.campaign_data.grouped_rewards"
                :key="rewardData.reward.id"
                class="flex justify-between items-center">
                <span class="text-gray-150">
                  {{ rewardData.reward.title }}
                  <span v-if="rewardData.count > 1" class="text-gray-150">× {{ rewardData.count }}</span>
                </span>
                <span class="text-gray-150">{{ formatCurrency(rewardData.subtotal) }}</span>
              </div>

              <!-- 運費 (如果有的話) -->
              <div v-if="shippingFee > 0" class="flex justify-between items-center">
                <span class="text-gray-150"> {{ $t('profiles.backed_campaigns.shipping_fee') }} </span>
                <span class="text-gray-150">{{ formatCurrency(shippingFee) }}</span>
              </div>

              <!-- 分隔線 -->
              <hr class="border-gray-200 my-2">

              <!-- 總計 -->
              <div class="flex justify-between items-center">
                <span class="text-18px font-bold text-gray-100"> {{ $t('profiles.backed_campaigns.total') }}</span>
                <span class="info-value font-bold text-lg">{{ formatCurrency(totalAmount) }}</span>
              </div>

              <!-- 日期 -->
              <div class="flex flex-col">
                <span class="text-gray-100 font-bold mb-1"> {{ $t('profiles.backed_campaigns.pledge_date') }}</span>
                <span class="text-gray-150">{{ formatDate(pledgeDate) }}</span>
              </div>

              <!-- Refund (authorized within capture window) -->
              <div v-if="isRefundable" class="flex flex-col gap-2 pt-2 border-t border-gray-200">
                <p class="text-12px text-gray-100">
                  {{ $t('profiles.backed_campaigns.refund_hint') }}
                </p>
                <button
                  @click="requestRefund"
                  :disabled="refundState.loading"
                  class="btn btn-secondary text-14px self-start disabled:opacity-50 disabled:cursor-not-allowed">
                  {{ refundState.loading ? $t('profiles.backed_campaigns.refund_processing') : $t('profiles.backed_campaigns.request_refund') }}
                </button>
                <p v-if="refundState.error" class="text-12px text-red-400">
                  {{ refundState.error }}
                </p>
              </div>
            </div>

            <!-- Shipping Info -->
            <div class="p-4 rounded-4px bg-gray-600 text-14px space-y-4">
              <div class="flex items-center justify-between">
                <p class="text-16px font-bold"> {{ $t('profiles.backed_campaigns.shipping_info') }} </p>
                <div class="flex items-center space-x-2">
                  <!-- 提示組件 -->
                  <nav-panel :panel-class="'w-[320px]'">
                    <template #icon>
                      <img
                        src="../../images/info-white.svg"
                        class="w-5 h-5 opacity-50"
                        alt="info"
                      />
                    </template>
                    <template #content>
                      <div class="custom-content text-[12px]">
                        <p class="font-bold mb-2 text-center">{{ $t('profiles.backed_campaigns.address_edit_policy') }}</p>
                        <p class="mb-1" v-html="$t('profiles.backed_campaigns.address_edit_description', { deadline: addressEditDeadlineText })"></p>
                      </div>
                    </template>
                  </nav-panel>

                  <button
                    @click="openShippingModal"
                    :disabled="!shippingAddressEditable"
                    aria-label="Edit Shipping"
                    class="group"
                    :class="{ 'opacity-50 cursor-not-allowed': !shippingAddressEditable }"
                  >
                    <img
                      src="../../images/icons/NotePencil.svg"
                      class="h-4 w-4 filter brightness-200 group-hover:grayscale group-hover:brightness-125"
                    />
                  </button>
                </div>
              </div>

              <template v-if="hasShippingInfo">
                <div class="flex flex-col">
                  <span class="text-gray-100 font-bold mb-1"> {{ $t('profiles.backed_campaigns.recipient') }}</span>
                  <span class="text-gray-150">{{ shippingInfo.name || backerName }}</span>
                </div>

                <div v-if="shippingInfo.phone" class="flex flex-col">
                  <span class="text-gray-100 font-bold mb-1"> {{ $t('profiles.backed_campaigns.phone_number') }}</span>
                  <span class="text-gray-150">{{ shippingInfo.phone }}</span>
                </div>

                <div class="flex flex-col">
                  <span class="text-gray-100 font-bold mb-1"> {{ $t('profiles.backed_campaigns.country_region') }}</span>
                  <span class="text-gray-150">{{ shippingInfo.country }}</span>
                </div>

                <div class="flex flex-col">
                  <span class="text-gray-100 font-bold mb-1"> {{ $t('profiles.backed_campaigns.zip_postal_code') }}</span>
                  <span class="text-gray-150">{{ shippingInfo.zip }}</span>
                </div>

                <div class="flex flex-col">
                  <span class="text-gray-100 font-bold mb-1"> {{ $t('profiles.backed_campaigns.state_province_region') }}</span>
                  <span class="text-gray-150">{{ shippingInfo.state }}</span>
                </div>

                <div class="flex flex-col">
                  <span class="text-gray-100 font-bold mb-1"> {{ $t('profiles.backed_campaigns.town_city') }}</span>
                  <span class="text-gray-150">{{ shippingInfo.city }}</span>
                </div>

                <div class="flex flex-col">
                  <span class="text-gray-100 font-bold mb-1"> {{ $t('profiles.backed_campaigns.address_line_1') }}</span>
                  <span class="text-gray-150">{{ shippingInfo.address1 }}</span>
                </div>

                <div v-if="shippingInfo.address2" class="flex flex-col">
                  <span class="text-gray-100 font-bold mb-1"> {{ $t('profiles.backed_campaigns.address_line_2') }}</span>
                  <span class="text-gray-150">{{ shippingInfo.address2 }}</span>
                </div>
              </template>

              <div v-else class="text-sm text-gray-400 italic">
                {{ $t('profiles.backed_campaigns.no_shipping_info') }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Shipping Modal -->
    <shipping-modal
      :isOpen="shippingModal.isOpen"
      :initialData="shippingInfo"
      :loading="shippingModal.loading"
      :error="shippingModal.error"
      @close="closeShippingModal"
      @submit="saveShippingInfo"
    />
  </div>
</template>

<script>
import { defineComponent, reactive, ref, computed, onMounted, getCurrentInstance } from "vue"
import { useClipboard } from '../utils/useClipboard.js'

export default defineComponent({
  setup() {
    const instance = getCurrentInstance()
    const $t = instance?.proxy?.$t || (() => '')
    const t = (key, options = {}) => {
      if (window.i18nextGlobal) {
        return window.i18nextGlobal(key, options)
      }
      return $t(key, options) || key
    }

    // 現有的 state 保留
    const state = reactive({
      loading: true,
      error: null,
      orderId: null
    })

    const orderData = ref(null)
    const clipboard = useClipboard()

    // 新增 Shipping Modal 狀態
    const shippingModal = reactive({
      isOpen: false,
      loading: false,
      error: null
    })

    const refundState = reactive({
      loading: false,
      error: null
    })

    // 狀態計算邏輯
    const ORDER_STATUS = {
      REFUND: {
        pending_refund: 'profiles.backed_campaigns.order_status.refund_pending',
        refunded: 'profiles.backed_campaigns.order_status.refunded'
      },
      ORDER: {
        created: 'profiles.backed_campaigns.order_status.pending_payment',
        cancelled: 'profiles.backed_campaigns.order_status.cancelled',
        interrupted: 'profiles.backed_campaigns.order_status.interrupted',
        pending_payment: 'profiles.backed_campaigns.order_status.pending_payment'
      },
      CAMPAIGN: {
        active: 'profiles.backed_campaigns.order_status.successfully_backed',
        draft: 'profiles.backed_campaigns.order_status.crowdfunding_success',
        prototype: 'profiles.backed_campaigns.order_status.in_production',
        production: 'profiles.backed_campaigns.order_status.manufacturing',
        shipping: 'profiles.backed_campaigns.order_status.shipped'
      }
    }
    const STATUS_COLORS = {
      SUCCESS: '#29cb00',
      PROGRESS: '#ffb72f',
      SHIPPING: '#4f84eb',
      REFUND: '#ff7474',
      CANCELLED: '#99a0a8',
      DEFAULT: '#99a0a8'
    }

    const getProductionStatusKey = (productionState) => {
      return ORDER_STATUS.CAMPAIGN[productionState] || ORDER_STATUS.CAMPAIGN.draft
    }

    const isCampaignEnded = (campaign) => {
      if (!campaign?.end_at) return true
      return new Date() > new Date(campaign.end_at)
    }

    const getPaidOrderStatusKey = (campaign) => {
      if (campaign?.visible) {
        return ORDER_STATUS.CAMPAIGN.active
      }
      if (isCampaignEnded(campaign)) {
        return getProductionStatusKey(campaign?.production_state)
      }
      return ORDER_STATUS.CAMPAIGN.draft
    }

    const getOrderStatusKey = (data) => {
      const order = data.order
      const campaign = data.campaign_data.campaign
      if (ORDER_STATUS.REFUND[order.refund_status]) {
        return ORDER_STATUS.REFUND[order.refund_status]
      }
      if (ORDER_STATUS.ORDER[order.state]) {
        return ORDER_STATUS.ORDER[order.state]
      }
      if (order.state === 'paid') {
        return getPaidOrderStatusKey(campaign)
      }
      return null
    }

    const getOrderStatus = (data) => {
      const order = data.order
      const statusKey = getOrderStatusKey(data)
      return statusKey ? t(statusKey) : order.state
    }

    const getStatusColorByKey = (statusKey) => {
      const colorMapping = {
        [ORDER_STATUS.CAMPAIGN.active]: STATUS_COLORS.SUCCESS,
        [ORDER_STATUS.CAMPAIGN.draft]: STATUS_COLORS.SUCCESS,
        [ORDER_STATUS.CAMPAIGN.prototype]: STATUS_COLORS.PROGRESS,
        [ORDER_STATUS.CAMPAIGN.production]: STATUS_COLORS.PROGRESS,
        [ORDER_STATUS.CAMPAIGN.shipping]: STATUS_COLORS.SHIPPING,
        [ORDER_STATUS.ORDER.pending_payment]: STATUS_COLORS.PROGRESS,
        [ORDER_STATUS.REFUND.pending_refund]: STATUS_COLORS.REFUND,
        [ORDER_STATUS.REFUND.refunded]: STATUS_COLORS.REFUND,
        [ORDER_STATUS.ORDER.cancelled]: STATUS_COLORS.CANCELLED,
        [ORDER_STATUS.ORDER.interrupted]: STATUS_COLORS.CANCELLED
      }
      return colorMapping[statusKey] || STATUS_COLORS.DEFAULT
    }

    const getStatusStyle = (data) => {
      const statusKey = getOrderStatusKey(data)
      const color = getStatusColorByKey(statusKey)
      return {
        color: color,
        backgroundColor: `color-mix(in srgb, ${color}, transparent 80%)`,
        borderColor: color
      }
    }

    // Computed properties
    const campaignTitle = computed(() =>
      orderData.value?.campaign_data?.campaign?.title || ''
    )

    const backerName = computed(() =>
      orderData.value?.backer_info?.display_name ||
      orderData.value?.order?.profile?.display_name || ''
    )

    const backerEmail = computed(() =>
      orderData.value?.backer_info?.email ||
      orderData.value?.order?.profile?.email || ''
    )

    const backerId = computed(() =>
      orderData.value?.order?.number || ''
    )

    const pledgeAmount = computed(() =>
      orderData.value?.campaign_data?.total_reward_price || 0
    )

    const totalAmount = computed(() =>
      orderData.value?.order?.total_price || 0
    )

    const pledgeDate = computed(() =>
      orderData.value?.order?.paid_at || orderData.value?.order?.created_at
    )

    const shippingInfo = computed(() => {
      const info = orderData.value?.shipping_info || {}
      return info
    })

    const hasShippingInfo = computed(() => {
      const hasInfo = shippingInfo.value && Object.keys(shippingInfo.value).length > 0
      return hasInfo
    })

    // Shipping address edit permission check
    const shippingAddressEditable = computed(() => {
      if (!orderData.value?.campaign_data?.campaign?.end_at) return false

      const campaignEndAt = new Date(orderData.value.campaign_data.campaign.end_at)
      const deadlineDate = new Date(campaignEndAt.getTime() + (7 * 24 * 60 * 60 * 1000)) // +7天
      const now = new Date()

      return now <= deadlineDate
    })

    // Address Edit Deadline
    const addressEditDeadlineText = computed(() => {
      if (!orderData.value?.campaign_data?.campaign?.end_at) return ''

      const campaignEndAt = new Date(orderData.value.campaign_data.campaign.end_at)
      const deadlineDate = new Date(campaignEndAt.getTime() + (7 * 24 * 60 * 60 * 1000))

      return formatDate(deadlineDate.toISOString())
    })


    const latestDeliveryDate = computed(() => {
      if (!orderData.value?.campaign_data?.grouped_rewards) return null

      const deliveryDates = orderData.value.campaign_data.grouped_rewards
        .map(rewardData => rewardData.reward.estimated_delivery_at)
        .filter(date => date) // 過濾掉 null/undefined
        .map(date => new Date(date))

      if (deliveryDates.length === 0) return null

      // 找出最晚的日期
      const latestDate = new Date(Math.max(...deliveryDates))
      return latestDate.toISOString()
    })

    const isRefundable = computed(() => orderData.value?.order?.refundable === true)

    const requestRefund = async () => {
      if (!isRefundable.value) return
      if (!window.confirm(t('profiles.backed_campaigns.refund_confirm'))) return

      try {
        refundState.loading = true
        refundState.error = null

        const orderId = getOrderIdFromUrl()
        const response = await fetch(`/api/v1/profile/backed_campaigns/${orderId}/refund`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          }
        })

        if (!response.ok) {
          const data = await response.json().catch(() => ({}))
          throw new Error(data.message || data.error || t('profiles.backed_campaigns.refund_failed'))
        }

        await fetchOrderDetail()
      } catch (error) {
        console.error('Error requesting refund:', error)
        refundState.error = error.message || t('profiles.backed_campaigns.refund_failed')
      } finally {
        refundState.loading = false
      }
    }

    const shippingFee = computed(() => {
      const rewardTotal = orderData.value?.campaign_data?.total_reward_price || 0
      const orderTotal = orderData.value?.order?.total_price || 0
      const fee = orderTotal - rewardTotal
      return fee > 0 ? fee : 0
    })

    const statusInfo = computed(() => {
      if (!orderData.value) {
        return { text: '', style: {} }
      }
      const style = getStatusStyle(orderData.value)
      return {
        text: getOrderStatus(orderData.value),
        style: style
      }
    })

    // Methods
    const getOrderIdFromUrl = () => {
      const pathParts = window.location.pathname.split('/')
      return pathParts[pathParts.length - 1]
    }

    const fetchOrderDetail = async () => {
      try {
        state.loading = true
        state.error = null

        const orderId = getOrderIdFromUrl()
        if (!orderId) throw new Error('Order ID not found')

        const response = await fetch(`/api/v1/profile/backed_campaigns/${orderId}`)
        if (!response.ok) {
          if (response.status === 404) throw new Error('Order not found')
          if (response.status === 403) throw new Error('Access denied')
          throw new Error('Failed to load order details')
        }

        const data = await response.json()
        orderData.value = data.data
      } catch (error) {
        console.error('Error fetching order detail:', error)
        state.error = error.message || 'Failed to load order details'
      } finally {
        state.loading = false
      }
    }

    // Shipping Modal Methods
    const openShippingModal = () => {
      if (!shippingAddressEditable.value) return
      shippingModal.isOpen = true
    }

    const closeShippingModal = () => {
      shippingModal.isOpen = false
      shippingModal.error = null
    }

    const saveShippingInfo = async (formData) => {
      try {
        shippingModal.loading = true
        shippingModal.error = null

        const orderId = getOrderIdFromUrl()
        const response = await fetch(`/api/v1/profile/backed_campaigns/${orderId}/shipping_info`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
          },
          body: JSON.stringify({
            shipping_info: formData
          })
        })

        if (!response.ok) {
          const errorData = await response.json()
          throw new Error(errorData.message || 'Failed to update shipping info')
        }

        // 重新載入訂單數據
        await fetchOrderDetail()
        closeShippingModal()

      } catch (error) {
        console.error('Error saving shipping info:', error)
        shippingModal.error = error.message
      } finally {
        shippingModal.loading = false
      }
    }

    // 格式化函數
    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD'
      }).format(amount)
    }

    const formatDate = (dateString) => {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    const goBack = () => {
      window.location.href = '/profile/backed_campaigns'
    }

    const copyOrderNumber = async () => {
      if (orderData.value && orderData.value.order) {
        await clipboard.copy(orderData.value.order.number)
      }
    }

    const goToCampaignComments = () => {
      if (orderData.value && orderData.value.campaign_data && orderData.value.campaign_data.campaign) {
        window.open(`/campaigns/${orderData.value.campaign_data.campaign.id}#comments`, '_blank')
      }
    }

    const getImageUrl = (imageData) => {
      if (!imageData) return ''
      if (typeof imageData === 'string') return imageData
      if (imageData && typeof imageData === 'object') {
        // 詳情頁使用高解析度圖片，優先 x3
        return imageData.x3 || imageData.x2 || imageData.x1 || imageData.url || ''
      }
      return ''
    }

    onMounted(() => {
      fetchOrderDetail()
    })

    return {
      // 現有的
      state,
      orderData,
      clipboard,
      formatCurrency,
      formatDate,
      goBack,
      copyOrderNumber,
      goToCampaignComments,
      getImageUrl,
      // 新增的
      shippingModal,
      campaignTitle,
      backerName,
      backerEmail,
      backerId,
      pledgeAmount,
      totalAmount,
      pledgeDate,
      shippingInfo,
      hasShippingInfo,
      shippingAddressEditable,
      addressEditDeadlineText,
      latestDeliveryDate,
      shippingFee,
      statusInfo,
      openShippingModal,
      closeShippingModal,
      saveShippingInfo,
      isRefundable,
      refundState,
      requestRefund,
      t
    }
  }
})
</script>